import {
  Col,
  Row,
  Select,
  Space,
  TableColumnsType,
  Tag,
  Transfer,
  TreeSelect,
} from 'antd';
import { TableRowSelection } from 'antd/lib/table/interface';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { RevxTable } from 'components';
import { RevxButton } from 'components/RevxButton';
import { MarkupTypeEnum, PartialMarkup } from 'domains/markupDomain';
import { Organization, PLATFORM_ORG_ID } from 'domains/orgDomain';
import { SimplifiedSKU } from 'domains/skuDomain';
import { useOrg } from 'hooks/useOrg';
import { cloneDeep, keyBy, union } from 'lodash-es';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { listToTree } from 'utils/misc';

type KeyedSku = SimplifiedSKU & { key: string };

type TreeNodeOrganization = Organization & {
  value: Organization['id'];
  title: Organization['name'];
  children?: TreeNodeOrganization[];
};

const SkuTransfer = () => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();
  const skuMarkup = useAppSelector((state) => state.markup.curSkuMarkup);
  const isRevshop = skuMarkup?.type === MarkupTypeEnum.REVSHOP_SKU;
  const [filterOrgId, setFilterOrgId] = useState<number>(PLATFORM_ORG_ID);
  const [filterSkuTypeId, setFilterSkuTypeId] = useState<number>(0);

  useEffect(() => {
    if (isRevshop) {
      dispatch.markup.getRevshopSkuMarkupSkus(filterOrgId);
    } else {
      dispatch.markup.getOrgSkuMarkupSkus(filterOrgId);
    }
  }, [dispatch.markup, filterOrgId, isRevshop]);

  const { noSkuMarkupSkus, withSkuMarkupSkus, curSkuMarkup } = useAppSelector(
    (state) => state.markup
  );

  const withMarkupSkus = useMemo(
    () =>
      withSkuMarkupSkus.find((m) => m.markupId === curSkuMarkup?.id)?.skus ||
      [],
    [curSkuMarkup?.id, withSkuMarkupSkus]
  );

  const [targetKeys, setTargetKeys] = useState<string[]>(
    withMarkupSkus.map((sku) => String(sku.id))
  );
  useEffect(() => {
    setTargetKeys(withMarkupSkus.map((sku) => String(sku.id)));
  }, [withMarkupSkus]);

  const loading = useAppSelector(
    (state) =>
      state.loading.effects.markup.getRevshopSkuMarkupSkus ||
      state.loading.effects.markup.getOrgSkuMarkupSkus
  );

  const sourceKeyedSkus = useMemo(
    () =>
      union(noSkuMarkupSkus, withMarkupSkus)
        .filter(
          (sku) => filterSkuTypeId === 0 || sku.typeId === filterSkuTypeId
        )
        .map((sku) => ({ ...sku, key: String(sku.id) } as KeyedSku)),
    [filterSkuTypeId, noSkuMarkupSkus, withMarkupSkus]
  );

  const org = useOrg();
  const skuTypes = useAppSelector((state) => state.app.skuTypes);

  // org selection
  const fullOrgList = useAppSelector((state) => state?.org?.list || []);
  const treeData: TreeNodeOrganization[] = useMemo(() => {
    const treeNodeList: TreeNodeOrganization[] = fullOrgList.map((o) => ({
      ...o,
      value: o.id,
      title: o.name,
      children: undefined,
    }));

    const orgMap = keyBy<TreeNodeOrganization>(treeNodeList, 'id');
    if (!isRevshop) {
      // TODO: this is to create a node for platform, change to proper solution later
      return [
        {
          ...orgMap[org!.id],
          value: PLATFORM_ORG_ID,
          title: 'Revshop, Inc',
          children: undefined,
        },
        orgMap[org!.id],
      ];
    } else {
      return listToTree<TreeNodeOrganization>(treeNodeList, orgMap);
    }
  }, [fullOrgList, isRevshop, org]);

  const columns: TableColumnsType<KeyedSku> = [
    {
      title: t('common.id'),
      dataIndex: 'id',
    },
    {
      title: t('common.name'),
      dataIndex: 'name',
    },
    {
      title: t('common.active'),
      dataIndex: 'active',
      render: (active) => (
        <Tag color={active ? 'green' : 'volcano'}>
          {active ? t('common.active') : t('common.inactive')}
        </Tag>
      ),
    },
  ];

  const onOrgChange = (orgId: number) => {
    setTargetKeys([]);
    setFilterOrgId(orgId);
  };

  const onSave = () => {
    const updated: PartialMarkup = cloneDeep(skuMarkup!);
    const orgSkuFilter = updated.orgSkuFilters?.find(
      (f) => f.orgId === filterOrgId
    );
    if (Boolean(orgSkuFilter)) {
      orgSkuFilter!.skuIds = targetKeys.map((k) => Number(k));
    } else {
      updated.orgSkuFilters?.push({
        orgId: filterOrgId,
        skuIds: targetKeys.map((k) => Number(k)),
      });
    }
    dispatch.markup.saveMarkup(updated);
  };

  return (
    <div>
      <Space direction="horizontal">
        <TreeSelect
          showSearch
          filterTreeNode={(inputValue, treeNode) =>
            treeNode!
              .title!.toString()
              .toLocaleLowerCase()
              .includes(inputValue.toLocaleLowerCase())
          }
          style={{ width: 200 }}
          dropdownMatchSelectWidth={300}
          value={filterOrgId}
          dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
          treeData={treeData}
          treeDefaultExpandAll
          onChange={onOrgChange}
          treeNodeFilterProp="title"
          placeholder="Select organization"
        />
        <Select<number>
          onChange={setFilterSkuTypeId}
          defaultValue={filterSkuTypeId}
          getPopupContainer={(e) => {
            return e as HTMLElement;
          }}
        >
          <Select.Option value={0}>
            {t('platform.markup.labels.allSkuTypes')}
          </Select.Option>
          {skuTypes.map((skuType) => (
            <Select.Option value={skuType.id} key={skuType.id}>
              {skuType.name}
            </Select.Option>
          ))}
        </Select>
      </Space>
      <Transfer
        titles={[
          t('platform.markup.labels.noMarkupSkus'),
          t('platform.markup.labels.appliedSkus'),
        ]}
        style={{ marginTop: 4, marginBottom: 16 }}
        showSelectAll={false}
        dataSource={sourceKeyedSkus}
        targetKeys={targetKeys}
        showSearch
        onChange={setTargetKeys}
        filterOption={(inputValue, item) =>
          item.name.toLowerCase().includes(inputValue.toLowerCase())
        }
      >
        {({
          filteredItems,
          onItemSelectAll,
          onItemSelect,
          selectedKeys: listSelectedKeys,
        }) => {
          const rowSelection: TableRowSelection<KeyedSku> = {
            onChange(selectedRowKeys: React.Key[]) {
              onItemSelectAll(
                selectedRowKeys.map((key) => key.toString()),
                true
              );
            },
            selectedRowKeys: listSelectedKeys,
          };

          return (
            <RevxTable
              rowSelection={rowSelection}
              columns={columns}
              dataSource={filteredItems}
              size="small"
              rowKey="key"
              pagination={{ showSizeChanger: false }}
              onRow={(sku) => ({
                onClick: () => {
                  onItemSelect(sku.key, !listSelectedKeys.includes(sku.key));
                },
              })}
              loading={loading}
            />
          );
        }}
      </Transfer>
      <Row>
        <Col>
          <RevxButton key="save" onClick={onSave} type="primary">
            {t('common.save')}
          </RevxButton>
        </Col>
      </Row>
    </div>
  );
};

export default SkuTransfer;
